import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Check,
  BookOpen,
  GraduationCap,
  Target,
  Zap,
  Brain,
  Save,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import api from '@/lib/api';

const subjects = [
  { id: 'matematica', name: 'Matemática', icon: '📐' },
  { id: 'portugues', name: 'Português', icon: '📖' },
  { id: 'ciencias', name: 'Ciências', icon: '🔬' },
  { id: 'historia', name: 'História', icon: '📜' },
  { id: 'geografia', name: 'Geografia', icon: '🌍' },
  { id: 'ingles', name: 'Inglês', icon: '🇬🇧' },
];

const grades = [
  { id: '6ano_ef', name: '6º Ano - Ensino Fundamental' },
  { id: '7ano_ef', name: '7º Ano - Ensino Fundamental' },
  { id: '8ano_ef', name: '8º Ano - Ensino Fundamental' },
  { id: '9ano_ef', name: '9º Ano - Ensino Fundamental' },
];

const difficulties = [
  { id: 'easy', name: 'Fácil', description: 'Conceitos básicos', color: 'bg-green-500' },
  { id: 'medium', name: 'Médio', description: 'Aplicação prática', color: 'bg-amber-500' },
  { id: 'hard', name: 'Difícil', description: 'Problemas complexos', color: 'bg-red-500' },
];

export default function QuizLiveAI() {
  const navigate = useNavigate();
  
  const [subject, setSubject] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [questionCount, setQuestionCount] = useState(5);
  const [topic, setTopic] = useState('');
  const [bnccSkill, setBnccSkill] = useState('');
  const [bnccSkills, setBnccSkills] = useState([]);
  
  const [generating, setGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [quizTitle, setQuizTitle] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (subject && gradeLevel) {
      loadBnccSkills();
    }
  }, [subject, gradeLevel]);

  const loadBnccSkills = async () => {
    try {
      const response = await api.get('/quiz-live/bncc/skills', {
        params: { subject, grade_level: gradeLevel }
      });
      setBnccSkills(response.data.skills || []);
    } catch (error) {
      console.error('Error loading BNCC skills:', error);
    }
  };

  const handleGenerate = async () => {
    if (!subject || !gradeLevel) {
      toast.error('Selecione disciplina e série');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/quiz-live/ai/generate-questions', {
        subject,
        grade_level: gradeLevel,
        difficulty,
        question_count: questionCount,
        topic: topic || null,
        bncc_skill: bnccSkill && bnccSkill !== 'all' ? bnccSkill : null
      });

      setGeneratedQuestions(response.data.questions);
      
      // Auto-generate title
      const subjectName = subjects.find(s => s.id === subject)?.name || subject;
      const gradeName = grades.find(g => g.id === gradeLevel)?.name.split(' - ')[0] || gradeLevel;
      setQuizTitle(`Quiz de ${subjectName} - ${gradeName}`);
      
      toast.success(`${response.data.count} questões geradas!`);
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar questões');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveQuiz = async () => {
    if (!quizTitle.trim()) {
      toast.error('Digite um título para o quiz');
      return;
    }

    setSaving(true);
    try {
      await api.post('/quiz-live/quizzes', {
        title: quizTitle,
        description: topic || `Quiz gerado por IA sobre ${subjects.find(s => s.id === subject)?.name}`,
        subject,
        grade_level: gradeLevel,
        questions: generatedQuestions,
        is_public: false
      });

      toast.success('Quiz salvo com sucesso!');
      navigate('/quiz-live');
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error('Erro ao salvar quiz');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = () => {
    setGeneratedQuestions([]);
    handleGenerate();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/quiz-live')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Quiz com IA</h1>
                <p className="text-white/80 text-sm">Gere questões automaticamente</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-green-400" />
                Configuração
              </CardTitle>
              <CardDescription className="text-gray-400">
                Defina os parâmetros para gerar as questões
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Subject Selection */}
              <div>
                <Label className="text-gray-300 mb-3 block">Disciplina *</Label>
                <div className="grid grid-cols-3 gap-2">
                  {subjects.map(s => (
                    <div
                      key={s.id}
                      onClick={() => setSubject(s.id)}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                        subject === s.id
                          ? 'border-green-500 bg-green-500/20'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <span className="text-2xl">{s.icon}</span>
                      <p className="text-white text-xs mt-1">{s.name}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Grade */}
              <div>
                <Label className="text-gray-300">Série *</Label>
                <Select value={gradeLevel} onValueChange={setGradeLevel}>
                  <SelectTrigger className="mt-2 bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Selecione a série" />
                  </SelectTrigger>
                  <SelectContent>
                    {grades.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* BNCC Skill */}
              {bnccSkills.length > 0 && (
                <div>
                  <Label className="text-gray-300">Habilidade BNCC (opcional)</Label>
                  <Select value={bnccSkill} onValueChange={setBnccSkill}>
                    <SelectTrigger className="mt-2 bg-white/10 border-white/20 text-white">
                      <SelectValue placeholder="Selecione uma habilidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as habilidades</SelectItem>
                      {bnccSkills.map(skill => (
                        <SelectItem key={skill.code} value={skill.code}>
                          <span className="font-mono text-purple-400">{skill.code}</span>
                          <span className="text-gray-400 ml-2 truncate">{skill.description.substring(0, 50)}...</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Difficulty */}
              <div>
                <Label className="text-gray-300 mb-3 block">Dificuldade</Label>
                <div className="grid grid-cols-3 gap-2">
                  {difficulties.map(d => (
                    <div
                      key={d.id}
                      onClick={() => setDifficulty(d.id)}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                        difficulty === d.id
                          ? `border-${d.color.replace('bg-', '')} ${d.color}/20`
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <div className={`w-3 h-3 rounded-full ${d.color} mx-auto mb-1`} />
                      <p className="text-white text-sm font-medium">{d.name}</p>
                      <p className="text-gray-400 text-xs">{d.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Question Count */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-gray-300">Quantidade de questões</Label>
                  <Badge variant="outline" className="border-green-500 text-green-400">
                    {questionCount} questões
                  </Badge>
                </div>
                <Slider
                  value={[questionCount]}
                  onValueChange={([v]) => setQuestionCount(v)}
                  min={3}
                  max={15}
                  step={1}
                  className="w-full"
                />
              </div>

              {/* Topic */}
              <div>
                <Label className="text-gray-300">Tópico específico (opcional)</Label>
                <Input
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Ex: Frações, Guerra Fria, Ecossistemas..."
                  className="mt-2 bg-white/10 border-white/20 text-white"
                />
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={generating || !subject || !gradeLevel}
                className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                data-testid="generate-ai-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Gerando com IA...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Gerar Questões
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Generated Questions Preview */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-400" />
                  Questões Geradas
                </CardTitle>
                {generatedQuestions.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRegenerate}
                    className="text-gray-400 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Regenerar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {generatedQuestions.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">
                    Configure os parâmetros e clique em "Gerar Questões"
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Quiz Title Input */}
                  <div className="p-4 bg-white/10 rounded-xl">
                    <Label className="text-gray-300 text-sm">Título do Quiz</Label>
                    <Input
                      value={quizTitle}
                      onChange={(e) => setQuizTitle(e.target.value)}
                      className="mt-2 bg-white/10 border-white/20 text-white"
                    />
                  </div>

                  {/* Questions List */}
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {generatedQuestions.map((q, index) => (
                      <div
                        key={q.id}
                        className="p-4 bg-white/5 rounded-xl border border-white/10"
                      >
                        <div className="flex items-start gap-3">
                          <span className="w-7 h-7 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center font-medium flex-shrink-0">
                            {index + 1}
                          </span>
                          <div className="flex-1">
                            <p className="text-white text-sm mb-3">{q.question}</p>
                            <div className="space-y-1">
                              {q.options.map((opt, optIndex) => (
                                <div
                                  key={opt.id}
                                  className={`flex items-center gap-2 text-xs p-2 rounded ${
                                    opt.is_correct
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'text-gray-400'
                                  }`}
                                >
                                  <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-xs">
                                    {String.fromCharCode(65 + optIndex)}
                                  </span>
                                  {opt.text}
                                  {opt.is_correct && <Check className="w-3 h-3 ml-auto" />}
                                </div>
                              ))}
                            </div>
                            {q.bncc_skill && (
                              <Badge variant="outline" className="mt-2 text-xs border-purple-500 text-purple-400">
                                {q.bncc_skill}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSaveQuiz}
                    disabled={saving}
                    className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600"
                    data-testid="save-ai-quiz-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        Salvar Quiz ({generatedQuestions.length} questões)
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
